import React from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import type { ObserveConnection } from './useApiObserve';
import { formatRelativeTime, sortConnections, statusDotColor } from './helpers';

type Props = {
  connections: ObserveConnection[];
  hasData: boolean;
  selectedId: string | null;
  onSelect: (c: ObserveConnection) => void;
};

function DirectionBadge({ direction }: { direction: string | null }) {
  if (!direction) return <span className="text-xs text-gray-400">—</span>;
  if (direction === 'outbound') return <span className="text-xs font-medium text-blue-700">↑ outbound</span>;
  if (direction === 'inbound') return <span className="text-xs font-medium text-blue-700">↓ inbound</span>;
  return <span className="text-xs font-medium text-blue-700">↕ both</span>;
}

export default function ConnectionGrid({ connections, hasData, selectedId, onSelect }: Props) {
  const sorted = sortConnections(connections);

  if (!hasData || sorted.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500">
        No rows in Timescale api-observe connections. Data Flow ingest history and System Health telemetry are different stores — this empty is this view, not platform-wide zero traffic.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {sorted.map((c) => {
        const spark = (c.calls_per_hour || []).map((h) => ({
          t: new Date(h.hour).getTime(),
          count: h.count,
        }));
        const selected = selectedId === c.connection_id;
        return (
          <button
            key={c.connection_id}
            type="button"
            onClick={() => onSelect(c)}
            className={`text-left rounded-xl border p-4 bg-white shadow-sm hover:shadow-md transition-shadow ${
              selected ? 'ring-2 ring-indigo-500 border-indigo-300' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: statusDotColor(c.status) }}
                />
                <span className="font-semibold text-gray-900 truncate">{c.label}</span>
              </div>
              <span
                className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${
                  c.type === 'mcp' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}
              >
                {c.type === 'mcp' ? 'MCP' : 'External API'}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
              {c.app_id && (
                <span className="px-2 py-0.5 rounded bg-gray-100 font-mono truncate max-w-full">{c.app_id}</span>
              )}
              <DirectionBadge direction={c.direction} />
            </div>

            <div className="mt-3 text-xs text-gray-500">
              Last seen {formatRelativeTime(c.last_seen)}
            </div>
            <div className="mt-1 text-sm text-gray-700">
              <span className="font-medium">{c.total_calls_24h}</span> calls / 24h
              {c.error_rate_24h != null ? (
                <span className="text-gray-500"> · {c.error_rate_24h}% errors</span>
              ) : (
                <span className="text-gray-400"> · error rate N/A</span>
              )}
            </div>

            {spark.length > 0 && (
              <div className="mt-3 h-10 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spark}>
                    <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#c7d2fe" strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
